import type { AuthUser } from '../services/auth';

export type AdminPermissionKey =
  | 'payments'
  | 'campaignPackages'
  | 'adminGuide'
  | 'adAnalytics'
  | 'billboardTools';

export const ALL_ADMIN_PERMISSIONS: AdminPermissionKey[] = [
  'payments',
  'campaignPackages',
  'adminGuide',
  'adAnalytics',
  'billboardTools',
];

export function getAdminPermissionsFromAuth(user: AuthUser): AdminPermissionKey[] {
  if (!user || typeof user !== 'object') return [];

  const raw = user as Record<string, unknown>;
  const inner =
    'user' in raw && raw.user && typeof raw.user === 'object'
      ? (raw.user as Record<string, unknown>)
      : raw;

  const permsValue = inner.adminPermissions;

  if (!Array.isArray(permsValue)) return [];

  const perms = (permsValue as unknown[])
    .filter((p): p is string => typeof p === 'string')
    .filter((p): p is AdminPermissionKey => (ALL_ADMIN_PERMISSIONS as string[]).includes(p));

  return perms;
}

export function isOwnerAdmin(user: AuthUser): boolean {
  if (!user || typeof user !== 'object') return false;

  const raw = user as Record<string, unknown>;
  const inner =
    'user' in raw && raw.user && typeof raw.user === 'object'
      ? (raw.user as Record<string, unknown>)
      : raw;

  if (inner.role !== 'admin') {
    return false;
  }

  const perms = getAdminPermissionsFromAuth(user);
  return ALL_ADMIN_PERMISSIONS.every((permission) => perms.includes(permission));
}

export function isAdminUser(user: AuthUser): boolean {
  if (!user || typeof user !== 'object') return false;

  const raw = user as Record<string, unknown>;
  const inner =
    'user' in raw && raw.user && typeof raw.user === 'object'
      ? (raw.user as Record<string, unknown>)
      : raw;

  return inner.role === 'admin';
}

export function hasAdminPermission(
  user: AuthUser,
  permission: AdminPermissionKey,
): boolean {
  if (!user || typeof user !== 'object') return false;

  const perms = getAdminPermissionsFromAuth(user);
  if (isOwnerAdmin(user)) return true;

  return perms.includes(permission);
}
